
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { VanityAPI } from '../services/api';
import { HighlightArticle } from '../types';
import { unescapeHtml } from '../utils/unescapeHtml';
import { sanitizeLimitedHtml } from '../utils/sanitizeLimitedHtml';
import SEOPage from '../components/SEOPage';
import seoGlobals from '../seo_globals.json';

const HighlightDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [article, setArticle] = useState<HighlightArticle | null>(null);
    const [loading, setLoading] = useState(true);
    const [hasHistory, setHasHistory] = useState(false);

    useEffect(() => {
        // Check if we have history state key (not default) to determine if we can go back
        if (window.history.length > 1 && location.key !== 'default') {
            setHasHistory(true);
        }
    }, [location]);

    useEffect(() => {
        if (slug) {
            VanityAPI.getPublicHighlight(slug)
                .then(setArticle)
                .catch(console.error)
                .finally(() => setLoading(false));
        }
    }, [slug]);

    if (loading) return <div className="h-screen flex items-center justify-center bg-vanity-white font-serif italic text-xl">Loading Article...</div>;
    if (!article) return <div className="h-screen flex items-center justify-center bg-vanity-white font-serif italic text-xl">Article Not Found</div>;

    return (
        <div className="animate-fadeIn bg-vanity-white pb-32">
            <SEOPage
                title={article.seoTitle || `${article.title} | VANITY`}
                description={article.seoDescription || seoGlobals.seo.defaultDescription}
                image={article.imageUrl}
            />
            {/* Header / Nav condition: only show if history exists */}
            {hasHistory && (
                <div className="pt-24 px-6 md:px-12 max-w-[1440px] mx-auto">
                    <button
                        onClick={() => navigate(-1)}
                        className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-black transition-colors cursor-pointer"
                    >
                        &larr; Back
                    </button>
                </div>
            )}

            {/* Hero Split Layout (1/3 Image - 2/3 Text) */}
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 mt-16 pb-10 border-b border-gray-100">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
                    {/* Left: Image (4 cols = 1/3) */}
                    {article.imageUrl ? (
                        <div className="lg:col-span-4 aspect-[3/4] w-full overflow-hidden shadow-2xl bg-gray-100 lg:sticky lg:top-32">
                            <img src={article.imageUrl} alt={article.heroAltText || article.title} className="w-full h-full object-cover" />
                        </div>
                    ) : (
                        <div className="lg:col-span-4 aspect-[3/4] w-full bg-gray-100 flex items-center justify-center text-gray-300">NO IMAGE</div>
                    )}

                    {/* Right: Title & Short Text (8 cols = 2/3) */}
                    <div className="lg:col-span-8 space-y-8">
                        <div>
                            {article.category && <span className="text-[11px] font-black uppercase tracking-[0.4em] text-vanity-pink block mb-4">{article.category}</span>}
                            <h1 className="font-serif text-5xl md:text-6xl mb-10 leading-tight text-gray-900">{article.title}</h1>
                        </div>

                        {/* Assuming 'excerpt' might be available or using first part of text as intro if needed. 
                            If no excerpt field exists on type yet, we might render summary or nothing. 
                            The user asked for "short text". I'll use a placeholder or excerpt if available. 
                            Let's check type. HighlightArticle has 'excerpt'. PERFECT. */}
                        {/* Summary / Excerpt */}
                        {article.text && (
                            <div
                                className="font-serif text-xl4 md:text-2xl italic leading-relaxed text-gray-500 border-l-2 border-vanity-pink pl-6"
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeLimitedHtml(unescapeHtml(article.text))
                                }}
                            />
                        )}


                        <div className="pt-8">
                            <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                <span>Published: {new Date(article.createdAt || Date.now()).toLocaleDateString()}</span>
                                {article.status && (
                                    <>
                                        <span>&middot;</span>
                                        <span className={article.status === 'published' ? 'text-green-600' : 'text-orange-400'}>{article.status}</span>
                                    </>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>




            {/* FULL WIDTH NARRATIVE CONTENT */}
            {article.content && (
                <div className="mt-10 max-w-[1000px] mx-auto px-6 font-sans text-vanity-black/70 leading-relaxed mb-12 space-y-6">
                    <div className="vanity-content">
                        <div
                            dangerouslySetInnerHTML={{
                                __html: sanitizeLimitedHtml(unescapeHtml(article.content || article.text)) || ''
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Content Modules */}
            {article.contentModules && article.contentModules.length > 0 && (
                <div className="mt-10 mx-auto px-6 space-y-[30px]">
                    {article.contentModules.map(m => (
                        <div key={m.id} className="mb-[30px] last:mb-0">
                            {m.type === 'text' ? (
                                <div className="max-w-[1000px] mx-auto text-left space-y-6 px-6 md:px-0">
                                    {m.title && <h2 className="font-serif text-4xl">{m.title}</h2>}
                                    {m.text && (
                                        <div className="vanity-content">
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: sanitizeLimitedHtml(unescapeHtml(m.text))
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center px-6 md:px-0">
                                    <div className={`space-y-6 md:col-span-8 ${m.imagePosition === 'left' ? 'md:order-2' : 'md:order-1'}`}>
                                        {m.title && <h2 className="font-serif text-4xl">{m.title}</h2>}
                                        {m.text && (
                                            <div className="vanity-content">
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: sanitizeLimitedHtml(unescapeHtml(m.text))
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className={`aspect-square bg-gray-100 overflow-hidden shadow-xl relative md:col-span-4 ${m.imagePosition === 'left' ? 'md:order-1' : 'md:order-2'}`}>
                                        {m.imageUrl && <img src={m.imageUrl} alt={m.altText || article.title} className="w-full h-full object-cover" />}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Gallery (Legacy Support if needed, but module system covers it mostly) */}
            {article.galleryImages && article.galleryImages.length > 0 && (
                <div className="mt-10 max-w-[1440px] mx-auto px-6">
                    <h3 className="text-center font-sans text-xs font-black uppercase tracking-widest text-gray-400 mb-12">Visual Gallery</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {article.galleryImages.map((img, i) => (
                            <div key={i} className="aspect-[3/4] bg-gray-100 shadow-xl overflow-hidden">
                                <img src={img} alt={`Gallery image ${i + 1} for ${article.title}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HighlightDetail;
